import { ref, watch, onScopeDispose } from 'vue';
import { getNotificationUnreadCounts } from '@/api/social';
import { normalizeUnreadCounts } from '@/utils/notification-map';
import { fetchWithRetry } from '@/utils/fetch-with-retry';
import { useAuth } from '@/composables/useAuth';
import { useDocumentPoll } from '@/composables/useDocumentPoll';

const POLL_INTERVAL_MS = 2000;
const FETCH_RETRIES = 2;
const FETCH_RETRY_DELAY_MS = 400;

export function useNotificationPoll() {
  const { isLoggedIn } = useAuth();
  const hasUnread = ref(false);
  let disposed = false;

  async function refreshUnreadCounts() {
    if (disposed || !isLoggedIn.value) {
      hasUnread.value = false;
      return;
    }
    try {
      const data = await fetchWithRetry(() => getNotificationUnreadCounts(), {
        retries: FETCH_RETRIES,
        delayMs: FETCH_RETRY_DELAY_MS,
        shouldAbort: () => disposed || !isLoggedIn.value,
      });
      if (disposed) return;
      const counts = normalizeUnreadCounts(data);
      hasUnread.value = counts.total > 0;
    } catch (err) {
      if (!disposed) {
        console.warn('[notifications] poll unread counts failed', err);
      }
    }
  }

  const poll = useDocumentPoll(refreshUnreadCounts, {
    intervalMs: POLL_INTERVAL_MS,
    enabled: () => isLoggedIn.value,
    watchSources: () => isLoggedIn.value,
    scope: 'notifications',
    skipIfBusy: true,
  });

  watch(isLoggedIn, (loggedIn, wasLoggedIn) => {
    if (!loggedIn) {
      hasUnread.value = false;
      return;
    }
    if (!wasLoggedIn) poll.tick();
  });

  onScopeDispose(() => {
    disposed = true;
    poll.stop();
  });

  return { hasUnread, refreshUnreadCounts };
}
